import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Building2,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  User as UserIcon,
  Loader2,
  Pencil,
  Trash2,
  Plus,
  CalendarClock,
  MessageSquare,
  Send,
  Film,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { clientsApi, projectsApi, locationsApi, attentionApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import LocationsMultiSelect from "@/components/LocationsMultiSelect";
import { ClientCoverPicker } from "@/pages/ClientsPage";
import PlanningCalendarPage from "@/pages/PlanningCalendarPage";

/**
 * ClientDetailPage — one dedicated area per client.
 *
 * Sections:
 *   - Header: name, contact, phone, email, address + Edit/Delete (admin)
 *   - Summary: active projects, upcoming shoots, upcoming task dues
 *   - Projects grid using the existing ProjectCard visual (imported)
 *   - "+ New Project for this Client" button that opens the standard
 *     New Project dialog pre-filled with this client's id + name
 */
export default function ClientDetailPage() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [attentionScores, setAttentionScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, ps, locs, atn] = await Promise.all([
        clientsApi.get(clientId),
        clientsApi.projects(clientId),
        locationsApi.list().catch(() => []),
        attentionApi.scores().catch(() => ({ scores: {} })),
      ]);
      setClient(c);
      setProjects(ps);
      setLocations(locs);
      setAttentionScores(atn?.scores || {});
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Client not found");
        navigate("/clients");
      } else {
        toast.error("Failed to load client");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const activeProjects = useMemo(
    () => projects.filter((p) => p.status !== "archived"),
    [projects],
  );
  const archivedProjects = useMemo(
    () => projects.filter((p) => p.status === "archived"),
    [projects],
  );

  const handleDelete = async () => {
    try {
      await clientsApi.remove(clientId);
      toast.success(`Deleted client "${client?.name}"`);
      // Signal every mounted sidebar / clients list to drop the row
      // immediately — no page refresh required.
      window.dispatchEvent(new CustomEvent("clients:changed"));
      navigate("/clients");
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-zinc-950 flex items-center justify-center">
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading client…
        </div>
      </div>
    );
  }

  if (!client) return null;
  const s = client.summary || {};

  return (
    <div className="min-h-full bg-zinc-950">
      <section
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-8 pb-6"
        data-testid="client-detail-page"
      >
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-400 mb-4"
          data-testid="client-detail-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Clients
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-overline text-amber-500/80">
              <Building2 className="w-3 h-3" /> Client
            </div>
            <h1
              data-testid="client-detail-name"
              className="font-heading text-3xl sm:text-4xl text-zinc-100 mt-1"
            >
              {client.name}
            </h1>
            <div className="mt-3 flex items-center gap-x-5 gap-y-1 flex-wrap text-sm text-zinc-400">
              {client.contact_name && (
                <span className="flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5" /> {client.contact_name}
                </span>
              )}
              {client.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  <a
                    href={`tel:${client.phone}`}
                    className="hover:text-amber-400"
                    data-testid="client-detail-phone"
                  >
                    {client.phone}
                  </a>
                </span>
              )}
              {client.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  <a
                    href={`mailto:${client.email}`}
                    className="hover:text-amber-400"
                    data-testid="client-detail-email"
                  >
                    {client.email}
                  </a>
                </span>
              )}
              {client.address && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {client.address}
                </span>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                data-testid="edit-client-btn"
                onClick={() => setEditOpen(true)}
                className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
              >
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="delete-client-btn"
                    className="bg-transparent border-zinc-800 text-rose-400 hover:bg-zinc-900"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete &ldquo;{client.name}&rdquo;?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-500">
                      Projects assigned to this client will keep their client name
                      as free text and can be re-linked later. The client record
                      itself will be permanently removed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-delete-client"
                      onClick={handleDelete}
                      className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </section>

      {/* Summary strip */}
      <section
        data-testid="client-summary"
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryTile
            testId="summary-active-projects"
            label="Active Projects"
            value={s.active_project_count || 0}
            tone="emerald"
          />
          <SummaryTile
            testId="summary-upcoming-shoots"
            label="Upcoming Shoots"
            value={s.upcoming_shoots || 0}
            tone="amber"
            hint={s.next_shoot_date ? `Next: ${s.next_shoot_date}` : "No shoots scheduled"}
          />
          <SummaryTile
            testId="summary-upcoming-tasks"
            label="Upcoming Tasks Due"
            value={s.upcoming_tasks_due || 0}
            tone="rose"
            hint={s.next_task_due_date ? `Next: ${s.next_task_due_date}` : "No tasks due"}
          />
        </div>
      </section>

      {/* Notes journal — internal users only */}
      {!me?.client_id && (
        <ClientNotesSection
          clientId={clientId}
          me={me}
          canDelete={isAdmin}
        />
      )}

      {/* Planning Calendar — soft-project workspace scoped to this client.
          Reuses the same page component in "embedded" mode so all creates,
          launches and attachments behave identically to the top-level view. */}
      <section
        data-testid="client-planning-calendar-section"
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-10"
      >
        <PlanningCalendarPage
          clientId={clientId}
          clientName={client.name}
          embedded
        />
      </section>

      {/* Projects grid */}
      <section
        data-testid="client-projects-section"
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-24"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-zinc-200">
            Projects <span className="text-zinc-600 font-normal">· {activeProjects.length}</span>
          </h2>
          {isAdmin && (
            <Button
              data-testid="new-project-for-client-btn"
              onClick={() => setNewProjectOpen(true)}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              <Plus className="w-4 h-4 mr-1" /> New Project for {client.name}
            </Button>
          )}
        </div>
        {activeProjects.length === 0 ? (
          <div
            data-testid="client-no-projects"
            className="text-center py-16 text-zinc-500 border border-dashed border-zinc-800 rounded-md"
          >
            <p className="mb-2">No projects for this client yet.</p>
            {isAdmin && (
              <button
                onClick={() => setNewProjectOpen(true)}
                className="text-amber-400 text-sm hover:underline"
              >
                Create the first one →
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeProjects.map((p, idx) => (
              <ProjectCard
                key={p.id}
                p={p}
                idx={idx}
                attention={attentionScores[p.id]}
                backTo={`/clients/${client.id}`}
                backLabel={client.name}
              />
            ))}
          </div>
        )}
        {archivedProjects.length > 0 && (
          <div className="mt-10">
            <h3 className="font-heading text-sm text-zinc-500 uppercase tracking-wider mb-3">
              Archived · {archivedProjects.length}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-90">
              {archivedProjects.map((p, idx) => (
                <ProjectCard
                  key={p.id}
                  p={p}
                  idx={idx}
                  attention={attentionScores[p.id]}
                  backTo={`/clients/${client.id}`}
                  backLabel={client.name}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {editOpen && (
        <EditClientDialog
          open={editOpen}
          setOpen={setEditOpen}
          client={client}
          onSaved={(updated) => {
            setClient((prev) => ({ ...prev, ...updated }));
            // If name changed, project card `client` strings for this client
            // were server-side rewritten too — reload to pick them up.
            load();
          }}
        />
      )}

      {newProjectOpen && (
        <NewProjectForClientDialog
          open={newProjectOpen}
          setOpen={setNewProjectOpen}
          client={client}
          locations={locations}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}

const TILE_TONE = {
  emerald: "border-emerald-500/30 text-emerald-300",
  amber: "border-amber-500/30 text-amber-300",
  rose: "border-rose-500/30 text-rose-300",
};

function SummaryTile({ label, value, tone, hint, testId }) {
  return (
    <div
      data-testid={testId}
      className={`border rounded-md p-4 bg-zinc-900/50 ${TILE_TONE[tone]}`}
    >
      <div className="text-overline text-zinc-500">{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
      {hint && (
        <div className="text-[11px] font-mono text-zinc-500 mt-1 flex items-center gap-1">
          <CalendarClock className="w-3 h-3" />
          {hint}
        </div>
      )}
    </div>
  );
}

// Reuse the same visual style as the main Projects page's ProjectCard.
// Kept intentionally simple here — only the fields the client detail
// page needs. Deep-links into the project like anywhere else.
const COVER_GRADIENTS = {
  amber: "from-amber-500/30 via-amber-700/10 to-zinc-900",
  rose: "from-rose-500/30 via-rose-700/10 to-zinc-900",
  teal: "from-teal-500/30 via-teal-700/10 to-zinc-900",
  violet: "from-violet-500/30 via-violet-700/10 to-zinc-900",
  blue: "from-blue-500/30 via-blue-700/10 to-zinc-900",
};

// Mirrors ProjectsPage — dynamic gradient that tracks the active white-
// label brand accent via CSS vars.
const BRAND_COVER_STYLE = {
  backgroundImage:
    "linear-gradient(135deg, var(--brand-accent-soft) 0%, var(--brand-accent-soft) 30%, rgba(24,24,27,0.9) 75%, rgb(24 24 27) 100%)",
};

// Mirrors the swatch list in ProjectsPage so the New-Project-for-Client
// dialog offers the same 5-tone palette plus the follow-brand option.
// Inline hex keeps swatches faithful under any active brand (the
// amber → brand-accent remap would otherwise repaint "amber").
const COLOR_OPTIONS = [
  { key: "amber",  swatch: "", inlineStyle: { backgroundColor: "#f59e0b" } },
  { key: "rose",   swatch: "", inlineStyle: { backgroundColor: "#f43f5e" } },
  { key: "teal",   swatch: "", inlineStyle: { backgroundColor: "#14b8a6" } },
  { key: "violet", swatch: "", inlineStyle: { backgroundColor: "#8b5cf6" } },
  { key: "blue",   swatch: "", inlineStyle: { backgroundColor: "#3b82f6" } },
  { key: "brand",  swatch: "", inlineStyle: { backgroundColor: "var(--brand-accent)" } },
];

// Mirrors STATUS_CHIP in ProjectsPage — kept in sync manually so a client's
// project cards look identical to the main Projects grid.
const STATUS_CHIP = {
  active:           { label: "Active",           cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  on_hold:          { label: "On Hold",          cls: "bg-amber-500/15   text-amber-300   border-amber-500/40" },
  completed:        { label: "Completed",        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  ready_to_archive: { label: "Ready To Archive", cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  archived:         { label: "Archived",         cls: "bg-zinc-500/15    text-zinc-300    border-zinc-500/40" },
  cancelled:        { label: "Cancelled",        cls: "bg-rose-500/15    text-rose-300    border-rose-500/40" },
};

const ATTENTION_STYLES = {
  low:    { dot: "bg-emerald-400", chip: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  medium: { dot: "bg-amber-400",   chip: "bg-amber-500/15   border-amber-500/40   text-amber-300"   },
  high:   { dot: "bg-rose-400 animate-pulse", chip: "bg-rose-500/15 border-rose-500/40 text-rose-300" },
};

function AttentionChip({ projectId, data }) {
  const style = ATTENTION_STYLES[data.level] || ATTENTION_STYLES.low;
  const tooltip = data.reasons?.length
    ? `Attention: ${data.score}\n${data.reasons.join("\n")}`
    : `Attention score: ${data.score}`;
  return (
    <div
      data-testid={`attention-chip-${projectId}`}
      data-attention-level={data.level}
      data-attention-score={data.score}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-full text-[10px] font-mono font-bold border ${style.chip}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {data.score}
    </div>
  );
}

// Client detail's project card — same visual as the main Projects grid
// (status chip, attention/risk chip, day count, job #, director, location,
// description). Kept in-file for simplicity but matches ProjectsPage's
// ProjectCard byte-for-byte where it matters visually.
function ProjectCard({ p, idx, attention, backTo, backLabel }) {
  const chip = STATUS_CHIP[p.status] || STATUS_CHIP.active;
  const isArchived = p.status === "archived";
  return (
    <Link
      to={`/projects/${p.id}`}
      // Preserve the client-scoped breadcrumb so ProjectShell's back
      // button returns here instead of jumping to All Projects.
      state={backTo ? { from: backTo, fromLabel: backLabel } : undefined}
      data-testid={`project-card-${p.id}`}
      data-project-status={p.status || "active"}
      style={{ animationDelay: `${idx * 60}ms` }}
      className="animate-fade-up group relative rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 hover:border-zinc-700 transition-all block"
    >
      <div
        className={`h-32 relative overflow-hidden ${
          p.cover_color === "brand"
            ? ""
            : `bg-gradient-to-br ${COVER_GRADIENTS[p.cover_color] || COVER_GRADIENTS.amber}`
        } ${isArchived ? "grayscale opacity-70" : ""}`}
        style={p.cover_color === "brand" ? BRAND_COVER_STYLE : undefined}
      >
        <div
          className="absolute top-3 left-3 text-overline text-zinc-300/80"
          data-testid={`project-card-subtitle-${p.id}`}
        >
          {p.shoot_date
            ? `Shoot · ${p.shoot_date}`
            : p.start_date
            ? `Start · ${p.start_date} · No shoots scheduled`
            : "Pre-production"}
        </div>
        {p.day_count > 1 && (
          <div
            data-testid={`project-day-count-${p.id}`}
            className="absolute bottom-2 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider bg-zinc-950/70 text-amber-300 border border-amber-500/40 backdrop-blur"
            title={`Multi-day shoot · ${p.day_count} shoot days`}
          >
            {p.day_count} shoot days
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          {p.job_number ? (
            <div className="text-overline text-amber-500/80 font-mono">
              Job #{p.job_number}
            </div>
          ) : (
            <span aria-hidden />
          )}
          <div
            data-testid={`project-card-status-${p.id}`}
            className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider border ${chip.cls}`}
            title={`Status: ${chip.label}`}
          >
            {chip.label}
          </div>
        </div>
        <h3 className="font-heading text-xl text-zinc-100 mb-1.5 truncate group-hover:text-amber-500 transition-colors">
          {p.name}
        </h3>
        <p className="text-sm text-zinc-500 line-clamp-2 min-h-[2.5rem]">
          {p.description || "No description"}
        </p>
        <div className="mt-4 flex items-center gap-4 text-xs text-zinc-500">
          {p.director && (
            <div className="flex items-center gap-1.5">
              <Film className="w-3.5 h-3.5" />
              <span>{p.director}</span>
            </div>
          )}
          {attention && attention.score > 0 && (
            <AttentionChip projectId={p.id} data={attention} />
          )}
          {p.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span className="truncate max-w-[120px]">{p.location}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Edit + New Project dialogs — kept in-file to keep this feature
// self-contained.
// ─────────────────────────────────────────────────────────────────────

function EditClientDialog({ open, setOpen, client, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "",
    contact_name: client.contact_name || "",
    address: client.address || "",
    phone: client.phone || "",
    email: client.email || "",
    cover_color: client.cover_color || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await clientsApi.update(client.id, form);
      // Name / cover changes should flow into the sidebar immediately.
      window.dispatchEvent(new CustomEvent("clients:changed"));
      toast.success("Client updated");
      onSaved?.(updated);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="edit-client-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>Edit Client</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Client Name</Label>
            <Input
              data-testid="edit-client-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Contact Name</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Email</Label>
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Accent Color <span className="text-zinc-600 normal-case">(auto = inherit from newest project)</span>
            </Label>
            <ClientCoverPicker
              value={form.cover_color}
              onChange={(k) => setForm({ ...form, cover_color: k })}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="edit-client-submit"
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewProjectForClientDialog({ open, setOpen, client, locations, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    client: client.name,      // legacy display string — kept in sync by backend
    client_id: client.id,     // FK — this is what the server actually uses
    job_number: "",
    director: "",
    location_ids: [],
    start_date: new Date().toISOString().slice(0, 10),
    shoot_date: "",
    description: "",
    // Inherit the client's brand tone so every project for this client
    // reads as part of the same visual family by default. The user can
    // still override per-project with the swatch picker below.
    cover_color: client.cover_color || "amber",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    setSaving(true);
    try {
      const created = await projectsApi.create(form);
      toast.success(`Project "${created.name}" created for ${client.name}`);
      onCreated?.(created);
      setOpen(false);
    } catch (err) {
      toast.error("Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="new-project-for-client-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            New Project for <span className="text-amber-400">{client.name}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Client info is pre-filled — you can change it later if needed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">
                Project Name <span className="text-rose-400">*</span>
              </Label>
              <Input
                data-testid="new-project-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Job #</Label>
              <Input
                value={form.job_number}
                onChange={(e) => setForm({ ...form, job_number: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Start Date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Shoot Date</Label>
              <Input
                type="date"
                value={form.shoot_date}
                onChange={(e) => setForm({ ...form, shoot_date: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                style={{ colorScheme: "dark" }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Director</Label>
            <Input
              value={form.director}
              onChange={(e) => setForm({ ...form, director: e.target.value })}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Locations</Label>
            <LocationsMultiSelect
              value={form.location_ids}
              onChange={(ids) => setForm({ ...form, location_ids: ids })}
              locations={locations}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-overline text-zinc-500">Cover Tone</Label>
            <div className="flex gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  type="button"
                  key={c.key}
                  data-testid={`client-new-project-color-${c.key}`}
                  onClick={() => setForm({ ...form, cover_color: c.key })}
                  style={c.inlineStyle}
                  title={c.key === "brand" ? "Follow brand" : c.key}
                  className={`w-8 h-8 rounded-sm transition-all ${c.swatch} ${
                    form.cover_color === c.key
                      ? "ring-2 ring-offset-2 ring-offset-zinc-950 ring-amber-500"
                      : "opacity-60 hover:opacity-100"
                  }`}
                />
              ))}
            </div>
            {client.cover_color && form.cover_color === client.cover_color && (
              <p className="text-xs text-zinc-500">
                Matched to <span className="text-zinc-300">{client.name}</span>&apos;s tone.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="new-project-for-client-submit"
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Create Project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Client Notes / Journal — internal-only feature. Any client-user hitting
// this page has already been redirected by the backend 403 gate; we
// simply avoid rendering the section when me.client_id is set as an
// additional guard.
// ─────────────────────────────────────────────────────────────────────

function ClientNotesSection({ clientId, me, canDelete }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  // `editingId` holds the entry currently being edited; `editingBody`
  // holds its in-flight textarea value. Only one entry can be edited at
  // a time — keeps the UI focused and prevents accidental drift.
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await clientsApi.journal.list(clientId);
      setEntries(list);
    } catch {
      // 403 for a client-user would land here; parent guard prevents that
      // path in practice. Any transient error we log softly.
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const entry = await clientsApi.journal.create(clientId, trimmed);
      setEntries((prev) => [entry, ...prev]);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await clientsApi.journal.remove(clientId, entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      toast.success("Note deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete note");
    }
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setEditingBody(entry.body || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingBody("");
  };

  const handleEditSave = async () => {
    const trimmed = editingBody.trim();
    if (!editingId || !trimmed || savingEdit) return;
    setSavingEdit(true);
    try {
      const updated = await clientsApi.journal.update(clientId, editingId, trimmed);
      setEntries((prev) => prev.map((e) => (e.id === editingId ? updated : e)));
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update note");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <section
      data-testid="client-notes-section"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-8"
    >
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-zinc-500" />
        <h2 className="font-heading text-lg text-zinc-200">
          Notes <span className="text-zinc-600 font-normal">· internal</span>
        </h2>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mb-4 flex gap-2 items-start"
        data-testid="new-note-form"
      >
        <Textarea
          data-testid="new-note-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Log a call, brief, or creative note. Only Studio users can see this."
          rows={2}
          className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 flex-1"
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits — handy for fast entry
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        <Button
          type="submit"
          disabled={saving || !body.trim()}
          data-testid="new-note-submit"
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
          Log
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading notes…
        </div>
      ) : entries.length === 0 ? (
        <div
          data-testid="notes-empty"
          className="text-sm text-zinc-500 italic border border-dashed border-zinc-800 rounded-md p-6 text-center"
        >
          No notes yet. Log calls, briefs, and creative feedback here.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="notes-list">
          {entries.map((entry) => {
            const isEditing = editingId === entry.id;
            // Author OR admin can edit. Delete stays admin-only (per canDelete prop).
            const canEdit = canDelete || entry.author_id === me?.id;
            return (
              <li
                key={entry.id}
                data-testid={`note-${entry.id}`}
                className="border border-zinc-800 bg-zinc-900/50 rounded-md p-4 group relative"
              >
                <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                  <span className="text-sm text-amber-400 font-medium">
                    {entry.author_name || "Unknown"}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                    {formatDateTime(entry.created_at)}
                  </span>
                  {entry.updated_at && entry.updated_at !== entry.created_at && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 italic"
                      title={`Edited ${formatDateTime(entry.updated_at)}`}
                    >
                      · edited
                    </span>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      data-testid={`edit-note-body-${entry.id}`}
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      rows={Math.max(2, editingBody.split("\n").length)}
                      className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 w-full"
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                          e.preventDefault();
                          handleEditSave();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        data-testid={`cancel-edit-note-${entry.id}`}
                        className="text-zinc-400 hover:text-zinc-200"
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleEditSave}
                        disabled={savingEdit || !editingBody.trim()}
                        data-testid={`save-edit-note-${entry.id}`}
                        className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                      >
                        {savingEdit ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Check className="w-3.5 h-3.5 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">
                    {entry.body}
                  </div>
                )}

                {!isEditing && (canEdit || canDelete) && (
                  <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && (
                      <button
                        type="button"
                        data-testid={`edit-note-${entry.id}`}
                        onClick={() => beginEdit(entry)}
                        className="text-zinc-500 hover:text-amber-400 p-1 rounded-sm"
                        title="Edit note"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        data-testid={`delete-note-${entry.id}`}
                        onClick={() => handleDelete(entry.id)}
                        className="text-zinc-500 hover:text-rose-400 p-1 rounded-sm"
                        title="Delete note"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

